/* eslint-disable no-undef */
import React, { useState, useRef, useEffect } from 'react';
import { useOTPAuth } from '../providers/OTPAuthProvider';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  Stack,
  CircularProgress,
  Link
} from '@mui/material';
import { ArrowBack, CheckCircle } from '@mui/icons-material';

interface OTPVerificationFormProps {
  email: string;
  onSuccess: () => void;
  onBack?: () => void;
}

const OTPVerificationForm: React.FC<OTPVerificationFormProps> = ({ 
  email, 
  onSuccess, 
  onBack 
}) => {
  const { verifyOTP, requestOTP } = useOTPAuth();
  const [otpCode, setOtpCode] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Setup resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => {
        setResendCooldown(resendCooldown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // Auto-focus first input on mount
  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const handleInputChange = (index: number, value: string) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) {
      return;
    }

    const newOtpCode = [...otpCode];
    newOtpCode[index] = value;
    setOtpCode(newOtpCode);

    // Auto-advance to next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all digits are entered
    if (value && index === 5 && newOtpCode.every(digit => digit)) {
      handleVerify(newOtpCode.join(''));
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLDivElement>) => {
    // Handle backspace
    if (e.key === 'Backspace' && !otpCode[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }

    // Handle paste
    if (e.key === 'v' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handlePaste();
    }
  };

  const handlePaste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      const digits = text.replace(/\D/g, '').slice(0, 6);
      
      if (digits.length === 6) {
        const newOtpCode = digits.split('');
        setOtpCode(newOtpCode);
        inputRefs.current[5]?.focus();
        
        // Auto-submit
        handleVerify(digits);
      }
    } catch (_err) {
      console.error('Failed to read clipboard:', _err);
    }
  };

  const handleVerify = async (code?: string) => {
    const fullCode = code || otpCode.join('');
    
    if (fullCode.length !== 6) {
      setError('Please enter all 6 digits');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const result = await verifyOTP(email, fullCode);
      
      if (result.success) {
        onSuccess();
      } else {
        setError(result.error || 'Invalid verification code');
        // Clear the code on error
        setOtpCode(['', '', '', '', '', '']);
        inputRefs.current[0]?.focus();
      }
    } catch (_err) {
      void _err;
      setError('Failed to verify code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    
    try {
      setError(null);
      setResendCooldown(60); // 60 second cooldown
      
      const result = await requestOTP(email);
      
      if (!result.success) {
        setError(result.message);
        setResendCooldown(0);
      }
    } catch (_err) {
      void _err;
      setError('Failed to resend code. Please try again.');
      setResendCooldown(0);
    }
  };

  return (
    <Box sx={{ maxWidth: 400, mx: 'auto' }}>
      {onBack && (
        <Box sx={{ mb: 2 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={onBack}
            size="small"
            sx={{ color: 'text.secondary' }}
          >
            Back
          </Button>
        </Box>
      )}
      
      <Typography variant="h5" align="center" gutterBottom sx={{ fontWeight: 600 }}>
        Enter Verification Code
      </Typography>
      <Typography variant="body2" align="center" color="text.secondary">
        We sent a 6-digit code to
      </Typography>
      <Typography align="center" sx={{ fontWeight: 500, mb: 3 }}>
        {email}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Stack spacing={3}>
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
          {otpCode.map((digit, index) => (
            <TextField
              key={index}
              inputRef={(el) => (inputRefs.current[index] = el)}
              type="text"
              inputProps={{
                maxLength: 1,
                style: { 
                  textAlign: 'center',
                  fontSize: '1.5rem',
                  fontWeight: 'bold',
                },
                inputMode: 'numeric',
              }}
              value={digit}
              onChange={(e) => handleInputChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={(e) => {
                e.preventDefault();
                handlePaste();
              }}
              disabled={loading}
              sx={{
                width: 48,
                '& .MuiOutlinedInput-root': {
                  height: 56,
                  '&.Mui-focused fieldset': {
                    borderColor: 'primary.main',
                    borderWidth: 2,
                  },
                },
              }}
            />
          ))}
        </Box>

        <Button
          fullWidth
          variant="contained"
          size="large"
          onClick={() => handleVerify()}
          disabled={loading || otpCode.some(digit => !digit)}
          endIcon={loading ? <CircularProgress size={20} color="inherit" /> : <CheckCircle />}
          sx={{
            bgcolor: '#16a34a',
            '&:hover': { bgcolor: '#15803d' },
            py: 1.5,
            textTransform: 'none',
            fontSize: '1rem',
          }}
        >
          {loading ? 'Verifying...' : 'Verify Code'}
        </Button>

        <Typography variant="body2" align="center" color="text.secondary">
          Didn't receive the code?{' '}
          {resendCooldown > 0 ? (
            <Typography component="span" color="text.disabled">
              Resend in {resendCooldown}s
            </Typography>
          ) : (
            <Link
              component="button"
              onClick={handleResend}
              sx={{ cursor: 'pointer', fontWeight: 500 }}
            >
              Resend Code
            </Link>
          )}
        </Typography>
      </Stack>

      <Typography 
        variant="caption" 
        align="center" 
        display="block" 
        sx={{ mt: 3, color: 'text.secondary' }}
      >
        The code expires in 10 minutes
      </Typography>
    </Box>
  );
};

export default OTPVerificationForm;